import React, { useEffect, useState } from 'react';

import { useFetch } from '../../../hooks/useFetch';
import { Beyond, Bouwdeel, IdRegistration, Installation, Vendor, Vendortype } from '../../../common/types';
import { beyondGateway } from '../../../common/consts';
import '../IdRegistrationPage.css';

type VendorProps = {
  registration: IdRegistration;
  registrations: IdRegistration[];
  bouwdeel?: Bouwdeel;
  updateRegistration: (name: string, value: Beyond | string | undefined | Vendor | Vendortype) => void;
};

export const VendorComponent = ({ registration, registrations, bouwdeel, updateRegistration }: VendorProps) => {
  const [beyond, setBeyond] = useState({ vendor: '', vendortype: '' });

  const [beyonds, setBeyonds] = useState<Beyond[]>([]);

  const { data: dataVendors } = useFetch<Vendor[]>('/api/vendor', {
    defaultValue: [],
  });
  const { data: dataVendortypes } = useFetch<Vendortype[]>('/api/vendortype', {
    defaultValue: [],
  });

  useEffect(() => {
    setBeyond(prevState => ({
      ...prevState,
      vendor: dataVendors.find(v => v.name === 'Chainless')?.id || '',
    }));
  }, [dataVendors]);

  useEffect(() => {
    setBeyond(prevState => ({
      ...prevState,
      vendortype: dataVendortypes.find(v => v.name === 'Beyond')?.id || '',
    }));
  }, [dataVendortypes]);

  useEffect(() => {
    if (registration.installation === Installation.BEYOND) {
      const vendor = dataVendors.find(v => v.name === 'Chainless');
      const vendortype = dataVendortypes.find(v => v.name === 'Beyond');
      setBeyond({
        vendor: vendor?.id || '',
        vendortype: vendortype?.id || '',
      });
      if (vendor) {
        updateRegistration('vendor', vendor);
      }
      if (vendortype) {
        updateRegistration('vendortype', vendortype);
      }
    }
  }, [registration.installation, dataVendors, dataVendortypes, updateRegistration]);

  useEffect(() => {
    setBeyonds(registrations.map(reg => reg.beyond).filter((beyond): beyond is Beyond => !!beyond));
  }, [registrations]);

  //Default vendor
  useEffect(() => {
    if (dataVendors.length > 0 && !registration.vendor) {
      updateRegistration('vendor', dataVendors[0]);
    }
  }, [dataVendors, updateRegistration, registration.vendor]);

  useEffect(() => {
    if (
      registration.vendor &&
      registration.vendortype &&
      registration.vendor.id === beyond.vendor &&
      registration.vendortype.id === beyond.vendortype
    ) {
      updateRegistration('installation', Installation.BEYOND);
    }
  }, [registration.vendor, registration.vendortype]);

  useEffect(() => {
    if(registration.installation !== Installation.BEYOND) {
      updateRegistration('beyond', undefined);
      return;
    }

    let networkNumber = -1;
    if(registration.beyond && registration.beyond.network_number) {
      networkNumber = registration.beyond.network_number;
    }
    if (bouwdeel && registration.gateway) {
      const firstPart = bouwdeel.doi_code;
      const secondPart = registration.gateway.split('.')[2].slice(-2).padStart(2, '0');

      const prefix = firstPart + secondPart;
      // Do not set new network number if the current one already is a valid beyond network number for the current bouwdeel and gateway
      if (registration.beyond && String(registration.beyond.network_number).startsWith(prefix)) {
        return;
      }

      const existingNumbers = beyonds
        .filter(beyond => String(beyond.network_number).startsWith(prefix))
        .map(beyond => String(beyond.network_number));
      let thirdPart = 0;
      while (existingNumbers.includes(prefix + thirdPart)) {
        thirdPart += 1;
      }
      networkNumber = +(prefix + thirdPart);
    }
    updateRegistration('beyond', {
      gateway: beyondGateway,
      network_number: networkNumber,
    });
  }, [bouwdeel, registration.gateway, registration.installation, beyonds]);

  const updateNetworkNumber = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    updateRegistration('beyond', {
      gateway: beyondGateway,
      network_number: numericValue ? parseInt(numericValue) : -1,
    });
  }

  return (
    <>
      <div className="property">
        <label>Vendor</label>
        <select
          disabled={registration.installation === Installation.BEYOND}
          name="vendor_id"
          data-testid="vendor-select"
          value={registration.vendor && registration.vendor.id}
          onChange={e =>
            updateRegistration(
              'vendor',
              dataVendors.find(v => v.id === e.target.value)
            )
          }>
          {dataVendors.map((option, index) => (
            <option key={index} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </div>
      <div className="property">
        <label>Vendor-Type</label>
        <select
          className={registration?.vendortype ? '' : 'required'}
          data-testid="vendortype-select"
          disabled={!registration.vendor || registration.installation === Installation.BEYOND}
          name="vendortype_id"
          value={registration.vendortype && registration.vendortype.id}
          onChange={e =>
            updateRegistration(
              'vendortype',
              dataVendortypes.find(v => v.id === e.target.value)
            )
          }>
          <option value="">Keuze</option>
          {dataVendortypes
            .filter(v => registration.vendor && v.vendor_id === registration.vendor.id)
            .map((option, index) => (
              <option key={index} value={option.id}>
                {option.name}
              </option>
            ))}
        </select>
      </div>
      {registration.beyond && (
        <div className="property">
          <label htmlFor="beyond-networknumber">Beyond Netwerknummer</label>
          <input
            type="number"
            id="beyond-networknumber"
            name="beyond-networknumber"
            data-testid="beyond-networknumber"
            disabled={true}
            value={registration.beyond.network_number}
            onChange={event => updateNetworkNumber(event.target.value)}
            title={registration.beyond.network_number === -1 ? 'Er moet een bouwdeel en gateway geselecteerd zijn.' : ''}
            className={registration.beyond.network_number === -1 ? 'error' : ''}></input>
        </div>
      )}
    </>
  );
};
